import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import {
  AuthData,
  clearAuthData,
  formatAuthErrorMessage,
  getStoredAuthData,
  isEmbeddedMode,
  resetEmbeddedAuthSession,
  subscribeToAuthUpdates,
} from "app/utils/auth";
import {
  getCachedDeliveryDashboardSettings,
  syncDeliveryDashboardSettings,
} from "app/utils/deliveryDashboardSettings";
import { BRANDING } from "app/utils/branding";
import { AuthContext } from "app/components/Auth/AuthContext";

interface RequireAuthProps {
  children: React.ReactElement;
}

const embeddedShellSx = {
  width: "100%",
  height: "100%",
  minHeight: "-webkit-fill-available",
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "#f5f6fa",
  gap: 2,
  px: 2,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
};

export function RequireAuth({ children }: RequireAuthProps) {
  const location = useLocation();
  const embedded = isEmbeddedMode();
  const [authData, setAuthData] = React.useState<AuthData | null>(() =>
    embedded ? null : getStoredAuthData()
  );
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = React.useState(false);
  const [showRetry, setShowRetry] = React.useState(false);
  const [authAttempt, setAuthAttempt] = React.useState(0);

  React.useEffect(() => {
    if (!embedded) {
      return;
    }

    document.documentElement.classList.add("delivery-dashboard-embedded");
    return () => {
      document.documentElement.classList.remove("delivery-dashboard-embedded");
    };
  }, [embedded]);

  React.useEffect(
    () =>
      subscribeToAuthUpdates((nextAuth) => {
        setAuthData(nextAuth);
      }),
    []
  );

  React.useEffect(() => {
    const token = authData?.access_token;
    if (!token || getCachedDeliveryDashboardSettings()) {
      return;
    }
    // Reload settings on page refresh; skip duplicate fetch after login/embedded auth.
    void syncDeliveryDashboardSettings(token);
  }, [authData?.access_token]);

  React.useEffect(() => {
    if (!embedded) {
      return;
    }

    let cancelled = false;

    const authenticate = async () => {
      clearAuthData();
      setAuthData(null);
      setIsAuthenticating(true);
      setAuthError(null);
      setShowRetry(false);

      try {
        const nextAuth = await resetEmbeddedAuthSession();
        if (cancelled) {
          return;
        }
        setAuthData(nextAuth);
        setAuthError(null);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setAuthData(null);
        setAuthError(formatAuthErrorMessage(error));
      } finally {
        if (!cancelled) {
          setIsAuthenticating(false);
        }
      }
    };

    void authenticate();

    return () => {
      cancelled = true;
    };
  }, [embedded, authAttempt]);

  React.useEffect(() => {
    if (!embedded || authData?.access_token) {
      setShowRetry(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowRetry(true);
    }, 10000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [embedded, authData?.access_token, authAttempt, isAuthenticating]);

  const handleRetryAuth = () => {
    setAuthAttempt((attempt) => attempt + 1);
  };

  const accessToken = authData?.access_token;

  if (accessToken) {
    return (
      <AuthContext.Provider value={authData}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {children}
        </Box>
      </AuthContext.Provider>
    );
  }

  if (embedded) {
    return (
      <Box sx={embeddedShellSx}>
        {isAuthenticating && !authError ? (
          <CircularProgress sx={{ color: "#7ec597" }} />
        ) : null}

        <Typography sx={{ color: "#625a57", textAlign: "center" }}>
          {authError
            ? authError
            : isAuthenticating
              ? BRANDING.signingIn
              : "Loading delivery dashboard..."}
        </Typography>

        {(showRetry || authError) && (
          <Button
            variant="contained"
            onClick={handleRetryAuth}
            disabled={isAuthenticating}
            sx={{
              mt: 0.5,
              bgcolor: "#5fa67a",
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
              "&:hover": { bgcolor: "#4d9164" },
            }}
          >
            {isAuthenticating ? "Retrying sign-in..." : "Retry sign-in"}
          </Button>
        )}

        {(showRetry || authError) && (
          <Typography
            variant="caption"
            sx={{ color: "#9ca3af", textAlign: "center", maxWidth: 320 }}
          >
            {BRANDING.retrySignInHint}
          </Typography>
        )}
      </Box>
    );
  }

  return <Navigate to="/login" replace state={{ from: location.pathname }} />;
}
